import React, { useState, useEffect } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, ReferenceLine,
} from 'recharts';
import { getBallsThrown, getBestSerija, getStatistikeProtivnika } from '../utils/statistika';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const KLUBOVI = ['KK Rad', 'KK Rad 2', 'ŽKK Olimp'];
const SEZONE = ['2025/2026', '2026/2027'];
const TAKMICENJA = ['Sva', 'Premijer liga', 'Prva liga RS', 'Kup RS', 'Kup BiH'];

function Izvjestaji({ igraci, utakmice, setAktivna, globalnaSezona }) {
  const [glavniTab, setGlavniTab] = useState('ekipa');
  const [filterSezona, setFilterSezona] = useState(globalnaSezona || '2025/2026');
  useEffect(() => { setFilterSezona(globalnaSezona); }, [globalnaSezona]);
  const [filterKlub, setFilterKlub] = useState(KLUBOVI[0]);
  const [filterTakmicenje, setFilterTakmicenje] = useState('Sva');
  const [ekipaTab, setEkipaTab] = useState('pregled');
  const [odabraniIgrac, setOdabraniIgrac] = useState('');
  const [otvoreniProtivnik, setOtvoreniProtivnik] = useState(null);

  const utakmiceFilter = utakmice.filter(u =>
    u.sezona === filterSezona &&
    u.klub === filterKlub &&
    (filterTakmicenje === 'Sva' || u.takmicenje === filterTakmicenje)
  );

  // Bez filtra takmičenja — za "Po takmičenjima" tab
  const utakmiceSveFilter = utakmice.filter(u => u.sezona === filterSezona && u.klub === filterKlub);

  // Igraci koji su nastupali za klub u ovoj sezoni (iz utakmica, ne iz igraci.klub)
  const igracIdsUUtakmicama = new Set();
  utakmiceSveFilter.forEach(u => (u.rezultati || []).forEach(r => { if (r.igracId) igracIdsUUtakmicama.add(r.igracId); }));
  const igraciKluba = [...igracIdsUUtakmicama].map(igracId => {
    const ig = igraci.find(i => i.id === igracId);
    const prvaUt = utakmiceSveFilter.find(u => u.rezultati?.some(r => r.igracId === igracId));
    const prviRez = prvaUt?.rezultati?.find(r => r.igracId === igracId);
    return { id: igracId, ime: ig?.ime || prviRez?.ime || 'Nepoznat', klub: filterKlub };
  }).sort((a, b) => a.ime.localeCompare(b.ime));

  // ── Ekipne statistike ───────────────────────────────────────────────────────

  const pobjede = utakmiceFilter.filter(u => u.ekipaBodovi === 2).length;
  const remiji = utakmiceFilter.filter(u => u.ekipaBodovi === 1).length;
  const porazi = utakmiceFilter.filter(u => u.ekipaBodovi === 0).length;
  const bodovi = utakmiceFilter.reduce((a, u) => a + (u.ekipaBodovi || 0), 0);
  const ukupnoSP = utakmiceFilter.reduce((a, u) => a + (u.ekipaSP || 0), 0);
  const ukupnoMP = utakmiceFilter.reduce((a, u) => a + (u.ekipaMP || 0), 0);
  const ukupnoCunjeva = utakmiceFilter.reduce((a, u) => a + (u.ekipaUkupno || 0), 0);

  const domace = utakmiceFilter.filter(u => u.domaci);
  const gostujuce = utakmiceFilter.filter(u => !u.domaci);
  const prosjek = utakmiceFilter.length > 0 ? Math.round(ukupnoCunjeva / utakmiceFilter.length) : 0;
  const prosjekDomaci = domace.length > 0 ? Math.round(domace.reduce((a, u) => a + (u.ekipaUkupno || 0), 0) / domace.length) : 0;
  const prosjekGost = gostujuce.length > 0 ? Math.round(gostujuce.reduce((a, u) => a + (u.ekipaUkupno || 0), 0) / gostujuce.length) : 0;

  const podaciGrafikona = [...utakmiceFilter]
    .sort((a, b) => a.datum?.localeCompare(b.datum))
    .map(u => ({
      kolo: 'K' + u.kolo,
      naziv: 'K' + u.kolo + ' vs ' + u.protivnik,
      cunjevi: u.ekipaUkupno || 0,
      sp: u.ekipaSP || 0,
      mp: u.ekipaMP || 0,
      bodovi: u.ekipaBodovi || 0,
      domaci: u.domaci,
      rezultat: u.ekipaBodovi === 2 ? 'Pobjeda' : u.ekipaBodovi === 1 ? 'Remi' : 'Poraz',
      id: u.id,
    }));

  const bestSerija = getBestSerija(utakmiceFilter);
  const statistikeProtivnika = getStatistikeProtivnika(utakmiceFilter);

  // ── Individualne statistike ─────────────────────────────────────────────────

  const getStatistikeIgraca = (igracId, utakmiceZa) => {
    const filtriraneUt = utakmiceZa || utakmiceFilter;
    let ukupnoKugli = 0, ukupnoCunj = 0, ukupnoPune = 0, ukupnoCis = 0;
    let ukupnoPromasaja = 0, ukupnoSPig = 0, ukupnoMPig = 0, maxUkupno = 0;
    let domaciKugli = 0, domaciCunj = 0, gostKugli = 0, gostCunj = 0;

    filtriraneUt.forEach(u => {
      const r = u.rezultati?.find(r => r.igracId === igracId);
      if (!r) return;
      const kugle = getBallsThrown(r);
      const cunjevi = r.ukupno || 0;
      ukupnoKugli += kugle;
      ukupnoCunj += cunjevi;
      ukupnoPune += r.ukupnoPune || 0;
      ukupnoCis += r.ukupnoCis || 0;
      ukupnoPromasaja += r.promasaji || 0;
      ukupnoSPig += r.sp || 0;
      ukupnoMPig += r.mp || 0;
      if (cunjevi > maxUkupno) maxUkupno = cunjevi;
      if (u.domaci) { domaciKugli += kugle; domaciCunj += cunjevi; }
      else { gostKugli += kugle; gostCunj += cunjevi; }
    });

    const prosjekUt = ukupnoKugli > 0 ? Math.round((ukupnoCunj / ukupnoKugli) * 120) : 0;
    const prosjekSet = Math.round(prosjekUt / 4);
    const prosjekDom = domaciKugli > 0 ? Math.round((domaciCunj / domaciKugli) * 120) : 0;
    const prosjekGos = gostKugli > 0 ? Math.round((gostCunj / gostKugli) * 120) : 0;
    const prosjekProm = ukupnoKugli > 0 ? Math.round((ukupnoPromasaja / ukupnoKugli) * 120 * 10) / 10 : 0;
    const brojUt = ukupnoKugli > 0 ? Math.round((ukupnoKugli / 120) * 10) / 10 : 0;

    return {
      ukupnoKugli, ukupnoCunjeva: ukupnoCunj, ukupnoPune, ukupnoCis,
      ukupnoPromasaja, ukupnoSP: ukupnoSPig, ukupnoMP: ukupnoMPig,
      maxUkupno, prosjekUt, prosjekSet, prosjekDomaci: prosjekDom,
      prosjekGost: prosjekGos, prosjekPromasaja: prosjekProm, brojUt,
    };
  };

  const utakmiceIgraca = odabraniIgrac
    ? utakmiceFilter.filter(u => u.rezultati?.some(r => r.igracId === odabraniIgrac))
        .sort((a, b) => a.datum?.localeCompare(b.datum))
    : [];

  const igracStats = odabraniIgrac ? getStatistikeIgraca(odabraniIgrac) : null;

  const podaciGrafIgrac = utakmiceIgraca.map(u => {
    const r = u.rezultati.find(r => r.igracId === odabraniIgrac);
    const kugle = getBallsThrown(r);
    const prosjekUt = kugle > 0 ? Math.round((r.ukupno / kugle) * 120) : 0;
    return {
      kolo: 'K' + u.kolo,
      naziv: 'K' + u.kolo + ' vs ' + u.protivnik,
      cunjevi: r?.ukupno || 0,
      prosjek: prosjekUt,
      sp: r?.sp || 0,
      mp: r?.mp || 0,
      bodovi: u.ekipaBodovi,
      domaci: u.domaci,
    };
  });

  const prosjekIgracLinija = podaciGrafIgrac.length > 0
    ? Math.round(podaciGrafIgrac.reduce((a, d) => a + d.cunjevi, 0) / podaciGrafIgrac.length)
    : 0;

  // ── PDF Exporti ─────────────────────────────────────────────────────────────

  // ── Tabela učinka igrača (reusable) ────────────────────────────────────────

  const getUcinakRedovi = (utakmiceZa) =>
    igraciKluba.map(ig => {
      const s = getStatistikeIgraca(ig.id, utakmiceZa);
      if (s.brojUt === 0) return null;
      return { ig, s };
    }).filter(Boolean).sort((a, b) => b.s.prosjekUt - a.s.prosjekUt);

  const renderUcinakTabela = (utakmiceZa, naziv) => {
    const redovi = getUcinakRedovi(utakmiceZa);
    if (redovi.length === 0) return null;
    const prosjekTima = utakmiceZa.length > 0
      ? Math.round(utakmiceZa.reduce((a, u) => a + (u.ekipaUkupno || 0), 0) / utakmiceZa.length)
      : 0;
    return (
      <div key={naziv} style={{ marginBottom: '28px' }}>
        <div style={{ fontSize: '13px', fontWeight: '700', color: '#e94560', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '10px', paddingBottom: '6px', borderBottom: '1px solid #2a2a4a' }}>
          {naziv} — {utakmiceZa.length} utakmica
        </div>
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ minWidth: '700px' }}>
              <thead>
                <tr>
                  <th style={{ textAlign: 'left' }}>Ime i prezime</th>
                  <th style={{ textAlign: 'center', width: '80px' }}>Br. ut.</th>
                  <th style={{ textAlign: 'center' }}>Pune</th>
                  <th style={{ textAlign: 'center' }}>Čišćenje</th>
                  <th style={{ textAlign: 'center' }}>Ukupno</th>
                  <th style={{ textAlign: 'center' }}>Promašaji</th>
                  <th style={{ textAlign: 'center', color: '#e94560' }}>Prosjek</th>
                  <th style={{ textAlign: 'center' }}>Pr. prom.</th>
                </tr>
              </thead>
              <tbody>
                {redovi.map(({ ig, s }) => (
                  <tr key={ig.id} style={{ cursor: 'pointer' }}
                    onClick={() => setAktivna({ strana: 'profil', igracId: ig.id })}>
                    <td style={{ fontWeight: '600', color: '#fff' }}>{ig.ime}</td>
                    <td style={{ textAlign: 'center', color: '#aaa' }}>{s.brojUt}</td>
                    <td style={{ textAlign: 'center' }}>{s.ukupnoPune}</td>
                    <td style={{ textAlign: 'center' }}>{s.ukupnoCis}</td>
                    <td style={{ textAlign: 'center', fontWeight: '700', color: '#fff' }}>{s.ukupnoCunjeva}</td>
                    <td style={{ textAlign: 'center', color: '#666688' }}>{s.ukupnoPromasaja}</td>
                    <td style={{ textAlign: 'center', fontWeight: '700', color: '#e94560', fontSize: '16px' }}>{s.prosjekUt}</td>
                    <td style={{ textAlign: 'center', color: '#666688' }}>{s.prosjekPromasaja}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr style={{ background: '#1a1a2e', borderTop: '2px solid #e94560' }}>
                  <td style={{ fontWeight: '700', color: '#fff' }}>PROSJEK TIMA</td>
                  <td style={{ textAlign: 'center', color: '#aaa' }}>{utakmiceZa.length}</td>
                  <td></td><td></td><td></td><td></td>
                  <td style={{ textAlign: 'center', fontWeight: '700', color: '#e94560', fontSize: '16px' }}>{prosjekTima}</td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      </div>
    );
  };

  // ── PDF export po takmičenjima ──────────────────────────────────────────────

  const exportPoTakmicenjimaPDF = () => {
    const doc = new jsPDF();
    const TAKMICENJA_PDF = ['Premijer liga', 'Prva liga RS', 'Kup RS', 'Kup BiH'];
    let y = 14;

    doc.setFontSize(16); doc.setFont('helvetica', 'bold');
    doc.text('Učinak igrača — ' + filterKlub + ' — ' + filterSezona, 14, y); y += 10;
    doc.setFontSize(10); doc.setFont('helvetica', 'normal');
    doc.text('Generisano: ' + new Date().toLocaleDateString('sr-RS'), 14, y); y += 8;

    const dodajSekciju = (utakmiceZa, naziv) => {
      const redovi = getUcinakRedovi(utakmiceZa);
      if (redovi.length === 0) return;
      const prosjekTima = utakmiceZa.length > 0
        ? Math.round(utakmiceZa.reduce((a, u) => a + (u.ekipaUkupno || 0), 0) / utakmiceZa.length) : 0;
      doc.setFontSize(12); doc.setFont('helvetica', 'bold');
      doc.text('UČINAK IGRAČA — ' + naziv.toUpperCase() + ' ' + filterSezona, 14, y + 6);
      autoTable(doc, {
        startY: y + 10,
        head: [['Ime i prezime', 'Br. ut.', 'Pune', 'Čišćenje', 'Ukupno', 'Promašaji', 'Prosjek', 'Pr.prom.']],
        body: redovi.map(({ ig, s }) => [ig.ime, s.brojUt, s.ukupnoPune, s.ukupnoCis, s.ukupnoCunjeva, s.ukupnoPromasaja, s.prosjekUt, s.prosjekPromasaja]),
        foot: [['PROSJEK TIMA', utakmiceZa.length, '', '', '', '', prosjekTima, '']],
        styles: { fontSize: 9, cellPadding: 3 },
        headStyles: { fillColor: [233, 69, 96], textColor: 255, fontStyle: 'bold' },
        footStyles: { fillColor: [50, 50, 80], textColor: 255, fontStyle: 'bold' },
        alternateRowStyles: { fillColor: [245, 245, 245] },
      });
      y = doc.lastAutoTable.finalY + 12;
    };

    TAKMICENJA_PDF.forEach(takm => {
      const ut = utakmiceSveFilter.filter(u => u.takmicenje === takm);
      if (ut.length > 0) dodajSekciju(ut, takm);
    });

    // Kumulativ
    if (utakmiceSveFilter.length > 0) dodajSekciju(utakmiceSveFilter, 'KUMULATIV SEZONA');

    doc.save(filterKlub.replace(/ /g, '_') + '_ucinak_' + filterSezona.replace('/', '-') + '.pdf');
  };

  const exportEkipaPDF = () => {
    const doc = new jsPDF();
    const naziv = filterKlub + ' — ' + filterSezona + (filterTakmicenje !== 'Sva' ? ' — ' + filterTakmicenje : '');
    doc.setFontSize(16); doc.setFont('helvetica', 'bold');
    doc.text('Izvjestaj — ' + naziv, 14, 18);
    doc.setFontSize(11); doc.setFont('helvetica', 'normal');
    doc.text('Utakmica: ' + utakmiceFilter.length + '  |  W/D/L: ' + pobjede + '/' + remiji + '/' + porazi + '  |  Bodova: ' + bodovi + '  |  Prosjek: ' + prosjek, 14, 28);

    const statsIgraci = igraciKluba.map(ig => {
      const s = getStatistikeIgraca(ig.id);
      return s.brojUt > 0 ? [ig.ime, s.brojUt, s.ukupnoPune, s.ukupnoCis, s.ukupnoCunjeva, s.ukupnoPromasaja, s.prosjekUt, s.prosjekPromasaja] : null;
    }).filter(Boolean).sort((a, b) => b[6] - a[6]);

    autoTable(doc, {
      startY: 35,
      head: [['Igrač', 'Br.ut.', 'Pune', 'Čišćenje', 'Ukupno', 'Promašaji', 'Prosjek', 'Pr.prom.']],
      body: statsIgraci,
      styles: { fontSize: 9, cellPadding: 3 },
      headStyles: { fillColor: [233, 69, 96], textColor: 255, fontStyle: 'bold' },
      alternateRowStyles: { fillColor: [245, 245, 245] },
      foot: [['PROSJEK TIMA', utakmiceFilter.length, '', '', '', '', prosjek, '']],
      footStyles: { fillColor: [50, 50, 80], textColor: 255, fontStyle: 'bold' },
    });
    doc.save(filterKlub.replace(/ /g, '_') + '_' + filterSezona.replace('/', '-') + '.pdf');
  };

  const exportIgracPDF = () => {
    if (!odabraniIgrac) return;
    const igrac = igraci.find(i => i.id === odabraniIgrac);
    if (!igrac) return;
    const doc = new jsPDF();
    doc.setFontSize(16); doc.setFont('helvetica', 'bold');
    doc.text('Statistike — ' + igrac.ime, 14, 18);
    doc.setFontSize(11); doc.setFont('helvetica', 'normal');
    doc.text(filterKlub + ' | ' + filterSezona + (filterTakmicenje !== 'Sva' ? ' | ' + filterTakmicenje : ''), 14, 28);

    const redovi = utakmiceIgraca.map(u => {
      const r = u.rezultati.find(r => r.igracId === odabraniIgrac);
      const kugle = getBallsThrown(r);
      const prosjekR = kugle > 0 ? Math.round((r.ukupno / kugle) * 120) : 0;
      return ['Kolo ' + u.kolo, u.datum, u.protivnik, u.domaci ? 'D' : 'G', u.takmicenje || 'PL',
        r.ukupnoPune || 0, r.ukupnoCis || 0, r.ukupno || 0, r.promasaji || 0, r.sp || 0, r.mp || 0, prosjekR];
    });

    autoTable(doc, {
      startY: 35,
      head: [['Kolo', 'Datum', 'Protivnik', 'D/G', 'Takm.', 'Pune', 'Čiš.', 'Ukupno', 'Prom.', 'SP', 'MP', 'Prosjek']],
      body: redovi,
      styles: { fontSize: 8, cellPadding: 2 },
      headStyles: { fillColor: [233, 69, 96], textColor: 255, fontStyle: 'bold' },
      alternateRowStyles: { fillColor: [245, 245, 245] },
    });

    if (igracStats) {
      const y = doc.lastAutoTable.finalY + 8;
      doc.setFontSize(9); doc.setFont('helvetica', 'bold');
      doc.text('UKUPNO: ' + igracStats.brojUt + ' ut. | Prosjek: ' + igracStats.prosjekUt +
        ' | D: ' + igracStats.prosjekDomaci + ' | G: ' + igracStats.prosjekGost +
        ' | Max: ' + igracStats.maxUkupno + ' | SP: ' + igracStats.ukupnoSP + ' | MP: ' + igracStats.ukupnoMP, 14, y);
    }
    doc.save(igrac.ime.replace(/ /g, '_') + '_statistike.pdf');
  };

  // ── Tooltip ─────────────────────────────────────────────────────────────────

  const CustomTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const d = payload[0]?.payload;
    return (
      <div style={{ background: '#16162a', border: '1px solid #2a2a4a', borderRadius: '8px', padding: '10px 14px' }}>
        <p style={{ color: '#e94560', fontWeight: '600', marginBottom: '4px', fontSize: '12px' }}>{d?.naziv} {d?.domaci ? '(D)' : '(G)'}</p>
        {payload.map(p => (
          <p key={p.dataKey} style={{ color: p.color, fontSize: '12px', margin: '2px 0' }}>
            {p.name}: <strong>{p.value}</strong>
          </p>
        ))}
        {d?.rezultat && (
          <p style={{ fontSize: '12px', margin: '2px 0', color: d.bodovi === 2 ? '#2ed573' : d.bodovi === 1 ? '#5378ff' : '#e94560' }}>
            {d.rezultat}
          </p>
        )}
      </div>
    );
  };

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <div>
      <div className="page-title">Izvještaji</div>

      {/* Filteri */}
      <div className="filter-bar">
        {SEZONE.map(s => (
          <button key={s} className={'filter-btn ' + (filterSezona === s ? 'active' : '')}
            onClick={() => setFilterSezona(s)}>{s}</button>
        ))}
      </div>
      <div className="filter-bar">
        {KLUBOVI.map(k => (
          <button key={k} className={'filter-btn ' + (filterKlub === k ? 'active' : '')}
            onClick={() => { setFilterKlub(k); setOdabraniIgrac(''); }}>{k}</button>
        ))}
      </div>
      <div className="filter-bar">
        {TAKMICENJA.map(t => (
          <button key={t} className={'filter-btn ' + (filterTakmicenje === t ? 'active' : '')}
            onClick={() => setFilterTakmicenje(t)}>{t}</button>
        ))}
      </div>

      {/* Glavni tabovi */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '20px' }}>
        {[{ id: 'ekipa', naziv: '🏆 Ekipni' }, { id: 'igrac', naziv: '👤 Individualni' }].map(t => (
          <button key={t.id}
            className={glavniTab === t.id ? 'btn-primary' : 'filter-btn'}
            style={{ fontSize: '14px', padding: '8px 20px' }}
            onClick={() => setGlavniTab(t.id)}>{t.naziv}</button>
        ))}
      </div>

      {/* ── EKIPNI ─────────────────────────────────────────────────────────── */}
      {glavniTab === 'ekipa' && (
        <div>
          {/* Sub-tabovi */}
          <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'wrap' }}>
            {[
              { id: 'pregled', naziv: 'Pregled sezone' },
              { id: 'grafikon', naziv: 'Grafikon kretanja' },
              { id: 'igraci', naziv: 'Statistike igrača' },
              { id: 'poTakmicenjima', naziv: 'Po takmičenjima' },
              { id: 'protivnici', naziv: 'Statistike protivnika' },
              { id: 'serije', naziv: 'Serije' },
            ].map(t => (
              <button key={t.id} className={'filter-btn ' + (ekipaTab === t.id ? 'active' : '')}
                onClick={() => setEkipaTab(t.id)}>{t.naziv}</button>
            ))}
          </div>

          {/* Pregled sezone */}
          {ekipaTab === 'pregled' && (
            <div>
              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '20px' }}>
                {[
                  { naziv: 'Utakmica', vrijednost: utakmiceFilter.length },
                  { naziv: 'Pobjede', vrijednost: pobjede, boja: '#2ed573' },
                  { naziv: 'Remiji', vrijednost: remiji, boja: '#5378ff' },
                  { naziv: 'Porazi', vrijednost: porazi, boja: '#e94560' },
                  { naziv: 'Bodova', vrijednost: bodovi },
                  { naziv: 'Ukupno SP', vrijednost: ukupnoSP },
                  { naziv: 'Ukupno MP', vrijednost: ukupnoMP },
                  { naziv: 'Prosjek', vrijednost: prosjek },
                  { naziv: 'Prosjek domaći', vrijednost: prosjekDomaci, boja: '#2ed573' },
                  { naziv: 'Prosjek gost', vrijednost: prosjekGost, boja: '#5378ff' },
                ].map(s => (
                  <div key={s.naziv} className="stat-card">
                    <div className="broj" style={s.boja ? { color: s.boja } : {}}>{s.vrijednost}</div>
                    <div className="naziv">{s.naziv}</div>
                  </div>
                ))}
              </div>

              {/* Tabela rezultata po kolima */}
              <div className="card" style={{ padding: 0, overflow: 'hidden', marginBottom: '16px' }}>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ minWidth: '580px' }}>
                    <thead>
                      <tr>
                        <th style={{ textAlign: 'center' }}>Kolo</th>
                        <th>Datum</th>
                        <th>Protivnik</th>
                        <th style={{ textAlign: 'center' }}>D/G</th>
                        <th style={{ textAlign: 'center' }}>Čunjeva</th>
                        <th style={{ textAlign: 'center' }}>SP</th>
                        <th style={{ textAlign: 'center' }}>MP</th>
                        <th style={{ textAlign: 'center' }}>Rezultat</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[...utakmiceFilter].sort((a, b) => b.datum?.localeCompare(a.datum)).map(u => (
                        <tr key={u.id} style={{ cursor: 'pointer' }}
                          onClick={() => setAktivna({ strana: 'detaljiUtakmice', utakmicaId: u.id })}>
                          <td style={{ textAlign: 'center', fontWeight: '700', color: '#e94560' }}>{u.kolo}</td>
                          <td>{u.datum}</td>
                          <td style={{ fontWeight: '500' }}>{u.protivnik}</td>
                          <td style={{ textAlign: 'center' }}>
                            <span style={{ fontSize: '11px', fontWeight: '700', padding: '2px 7px', borderRadius: '4px', background: u.domaci ? 'rgba(46,213,115,0.15)' : 'rgba(83,120,255,0.15)', color: u.domaci ? '#2ed573' : '#5378ff' }}>
                              {u.domaci ? '🏠 D' : '✈️ G'}
                            </span>
                          </td>
                          <td style={{ textAlign: 'center', fontWeight: '600', color: '#fff' }}>{u.ekipaUkupno}</td>
                          <td style={{ textAlign: 'center', color: '#2ed573' }}>{u.ekipaSP || 0}</td>
                          <td style={{ textAlign: 'center', color: '#5378ff' }}>{u.ekipaMP}:{u.protivnikMP ?? (8 - u.ekipaMP)}</td>
                          <td style={{ textAlign: 'center' }}>
                            <span className={'badge ' + (u.ekipaBodovi === 2 ? 'badge-green' : u.ekipaBodovi === 1 ? 'badge-blue' : 'badge-red')}>
                              {u.ekipaBodovi === 2 ? 'Pobjeda' : u.ekipaBodovi === 1 ? 'Remi' : 'Poraz'} ({u.ekipaBodovi})
                            </span>
                          </td>
                        </tr>
                      ))}
                      {utakmiceFilter.length === 0 && (
                        <tr><td colSpan="8" style={{ textAlign: 'center', padding: '30px', color: '#666688' }}>Nema utakmica</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
              <button className="btn-primary" onClick={exportEkipaPDF}>📄 Export PDF — ekipa</button>
            </div>
          )}

          {/* Grafikon */}
          {ekipaTab === 'grafikon' && (
            <div>
              {podaciGrafikona.length === 0 ? (
                <div className="card empty-state"><div className="icon">📊</div><p>Nema podataka.</p></div>
              ) : (
                <>
                  <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '16px' }}>
                    {[
                      { naziv: 'Prosjek', vrijednost: prosjek },
                      { naziv: 'Domaći', vrijednost: prosjekDomaci, boja: '#2ed573' },
                      { naziv: 'Gost', vrijednost: prosjekGost, boja: '#5378ff' },
                      { naziv: 'Maksimum', vrijednost: Math.max(...podaciGrafikona.map(d => d.cunjevi)) },
                      { naziv: 'Minimum', vrijednost: Math.min(...podaciGrafikona.map(d => d.cunjevi)) },
                    ].map(s => (
                      <div key={s.naziv} className="stat-card">
                        <div className="broj" style={s.boja ? { color: s.boja } : {}}>{s.vrijednost}</div>
                        <div className="naziv">{s.naziv}</div>
                      </div>
                    ))}
                  </div>
                  <div className="card">
                    <div className="section-title">Kretanje čunjeva — {filterKlub}</div>
                    <ResponsiveContainer width="100%" height={300}>
                      <LineChart data={podaciGrafikona} margin={{ top: 10, right: 20, left: 0, bottom: 60 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#2a2a4a" />
                        <XAxis dataKey="kolo" stroke="#666688" fontSize={11} angle={-45} textAnchor="end" interval={0} />
                        <YAxis stroke="#666688" fontSize={12} domain={['auto', 'auto']} />
                        <Tooltip content={<CustomTooltip />} />
                        <ReferenceLine y={prosjek} stroke="#5378ff" strokeDasharray="6 3"
                          label={{ value: 'Prosjek: ' + prosjek, fill: '#5378ff', fontSize: 11, position: 'right' }} />
                        <Line type="monotone" dataKey="cunjevi" stroke="#e94560" strokeWidth={2.5} name="Čunjeva"
                          dot={(props) => {
                            const { cx, cy, payload } = props;
                            const boja = payload.bodovi === 2 ? '#2ed573' : payload.bodovi === 1 ? '#5378ff' : '#e94560';
                            return <circle key={'dot-' + payload.kolo} cx={cx} cy={cy} r={5} fill={boja} stroke={boja} />;
                          }}
                          activeDot={{ r: 8 }} />
                      </LineChart>
                    </ResponsiveContainer>
                    <div style={{ display: 'flex', gap: '16px', marginTop: '8px', fontSize: '12px', color: '#666688' }}>
                      <span><span style={{ color: '#2ed573' }}>●</span> Pobjeda</span>
                      <span><span style={{ color: '#5378ff' }}>●</span> Remi</span>
                      <span><span style={{ color: '#e94560' }}>●</span> Poraz</span>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Statistike igrača ekipe */}
          {ekipaTab === 'igraci' && (
            <div>
              <div className="card" style={{ padding: 0, overflow: 'hidden', marginBottom: '16px' }}>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ minWidth: '800px' }}>
                    <thead>
                      <tr>
                        <th>Igrač</th>
                        <th style={{ textAlign: 'center' }}>Br. ut.</th>
                        <th style={{ textAlign: 'center' }}>Pune</th>
                        <th style={{ textAlign: 'center' }}>Čišćenje</th>
                        <th style={{ textAlign: 'center' }}>Ukupno</th>
                        <th style={{ textAlign: 'center' }}>Promašaji</th>
                        <th style={{ textAlign: 'center', color: '#e94560' }}>Prosjek</th>
                        <th style={{ textAlign: 'center' }}>Domaći</th>
                        <th style={{ textAlign: 'center' }}>Gost</th>
                        <th style={{ textAlign: 'center' }}>Max</th>
                        <th style={{ textAlign: 'center' }}>SP</th>
                        <th style={{ textAlign: 'center' }}>MP</th>
                      </tr>
                    </thead>
                    <tbody>
                      {igraciKluba.map(ig => {
                        const s = getStatistikeIgraca(ig.id);
                        if (s.brojUt === 0) return null;
                        return (
                          <tr key={ig.id} style={{ cursor: 'pointer' }}
                            onClick={() => setAktivna({ strana: 'profil', igracId: ig.id })}>
                            <td style={{ fontWeight: '600', color: '#fff' }}>{ig.ime}</td>
                            <td style={{ textAlign: 'center' }}>{s.brojUt}</td>
                            <td style={{ textAlign: 'center' }}>{s.ukupnoPune}</td>
                            <td style={{ textAlign: 'center' }}>{s.ukupnoCis}</td>
                            <td style={{ textAlign: 'center', fontWeight: '700', color: '#fff' }}>{s.ukupnoCunjeva}</td>
                            <td style={{ textAlign: 'center', color: '#666688' }}>{s.ukupnoPromasaja}</td>
                            <td style={{ textAlign: 'center', fontWeight: '700', color: '#e94560', fontSize: '16px' }}>{s.prosjekUt}</td>
                            <td style={{ textAlign: 'center', color: '#2ed573', fontWeight: '600' }}>{s.prosjekDomaci || '—'}</td>
                            <td style={{ textAlign: 'center', color: '#5378ff', fontWeight: '600' }}>{s.prosjekGost || '—'}</td>
                            <td style={{ textAlign: 'center' }}>{s.maxUkupno}</td>
                            <td style={{ textAlign: 'center', color: '#2ed573' }}>{s.ukupnoSP}</td>
                            <td style={{ textAlign: 'center', color: '#5378ff' }}>{s.ukupnoMP}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot>
                      <tr style={{ background: '#1a1a2e', borderTop: '2px solid #e94560' }}>
                        <td style={{ fontWeight: '700', color: '#fff' }}>PROSJEK TIMA</td>
                        <td style={{ textAlign: 'center' }}>{utakmiceFilter.length}</td>
                        <td></td><td></td><td></td><td></td>
                        <td style={{ textAlign: 'center', fontWeight: '700', color: '#e94560', fontSize: '16px' }}>{prosjek}</td>
                        <td style={{ textAlign: 'center', color: '#2ed573', fontWeight: '600' }}>{prosjekDomaci || '—'}</td>
                        <td style={{ textAlign: 'center', color: '#5378ff', fontWeight: '600' }}>{prosjekGost || '—'}</td>
                        <td></td><td></td><td></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
              <button className="btn-primary" onClick={exportEkipaPDF}>📄 Export PDF — ekipa</button>
            </div>
          )}

          {/* Po takmičenjima */}
          {ekipaTab === 'poTakmicenjima' && (
            <div>
              {utakmiceSveFilter.length === 0 ? (
                <div className="card empty-state"><div className="icon">📊</div><p>Nema utakmica za {filterKlub} — {filterSezona}</p></div>
              ) : (
                <>
                  {['Premijer liga', 'Prva liga RS', 'Kup RS', 'Kup BiH'].map(takm => {
                    const ut = utakmiceSveFilter.filter(u => u.takmicenje === takm);
                    return renderUcinakTabela(ut, takm);
                  })}
                  {renderUcinakTabela(utakmiceSveFilter, 'Kumulativ — sve sezone')}
                  <button className="btn-primary" onClick={exportPoTakmicenjimaPDF}>
                    📄 Export PDF — učinak po takmičenjima
                  </button>
                </>
              )}
            </div>
          )}

          {/* Protivnici */}
          {ekipaTab === 'protivnici' && (
            <div>
              {statistikeProtivnika.length === 0 ? (
                <div className="card empty-state"><div className="icon">🎳</div><p>Nema podataka o protivnicima.</p></div>
              ) : (
                <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ minWidth: '680px' }}>
                      <thead>
                        <tr>
                          <th>Protivnik</th>
                          <th style={{ textAlign: 'center' }}>Odigrano</th>
                          <th style={{ textAlign: 'center', color: '#2ed573' }}>Pobjede</th>
                          <th style={{ textAlign: 'center', color: '#5378ff' }}>Remiji</th>
                          <th style={{ textAlign: 'center', color: '#e94560' }}>Porazi</th>
                          <th style={{ textAlign: 'center' }}>Naš prosjek</th>
                          <th style={{ textAlign: 'center' }}>Protivnik prosjek</th>
                        </tr>
                      </thead>
                      <tbody>
                        {statistikeProtivnika.map((p, i) => (
                          <React.Fragment key={i}>
                            <tr style={{ cursor: 'pointer' }}
                              onClick={() => setOtvoreniProtivnik(otvoreniProtivnik === p.protivnik ? null : p.protivnik)}>
                              <td style={{ fontWeight: '600', color: '#fff' }}>
                                {otvoreniProtivnik === p.protivnik ? '▾ ' : '▸ '}{p.protivnik}
                              </td>
                              <td style={{ textAlign: 'center' }}>{p.odigrano}</td>
                              <td style={{ textAlign: 'center', color: '#2ed573', fontWeight: '600' }}>{p.pobjede}</td>
                              <td style={{ textAlign: 'center', color: '#5378ff', fontWeight: '600' }}>{p.remiji}</td>
                              <td style={{ textAlign: 'center', color: '#e94560', fontWeight: '600' }}>{p.porazi}</td>
                              <td style={{ textAlign: 'center', fontWeight: '700', color: '#fff' }}>
                                {p.odigrano > 0 ? Math.round(p.ukupnoCunjeva / p.odigrano) : 0}
                              </td>
                              <td style={{ textAlign: 'center', color: '#666688' }}>
                                {p.odigrano > 0 ? Math.round(p.ukupnoProtivnik / p.odigrano) : 0}
                              </td>
                            </tr>
                            {otvoreniProtivnik === p.protivnik && utakmiceFilter
                              .filter(u => u.protivnik === p.protivnik)
                              .sort((a, b) => b.datum?.localeCompare(a.datum))
                              .map(u => (
                                <tr key={u.id} style={{ background: '#0f0f1a', cursor: 'pointer' }}
                                  onClick={() => setAktivna({ strana: 'detaljiUtakmice', utakmicaId: u.id })}>
                                  <td style={{ paddingLeft: '28px', color: '#666688', fontSize: '12px' }}>
                                    Kolo {u.kolo} • {u.datum} • {u.domaci ? 'Domaći' : 'Gost'}
                                  </td>
                                  <td colSpan="2" style={{ textAlign: 'center', color: '#aaa', fontSize: '12px' }}>
                                    {u.ekipaUkupno} : {u.protivnikCunjevi || '?'}
                                  </td>
                                  <td colSpan="2" style={{ textAlign: 'center', color: '#aaa', fontSize: '12px' }}>
                                    MP {u.ekipaMP}:{u.protivnikMP ?? (8 - u.ekipaMP)}
                                  </td>
                                  <td colSpan="2" style={{ textAlign: 'center' }}>
                                    <span className={'badge ' + (u.ekipaBodovi === 2 ? 'badge-green' : u.ekipaBodovi === 1 ? 'badge-blue' : 'badge-red')} style={{ fontSize: '11px' }}>
                                      {u.ekipaBodovi === 2 ? 'Pobjeda' : u.ekipaBodovi === 1 ? 'Remi' : 'Poraz'}
                                    </span>
                                  </td>
                                </tr>
                              ))}
                          </React.Fragment>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Serije */}
          {ekipaTab === 'serije' && (
            <div>
              {utakmiceFilter.length === 0 ? (
                <div className="card empty-state"><div className="icon">🏆</div><p>Nema utakmica.</p></div>
              ) : (
                <>
                  <div style={{ display: 'flex', gap: '14px', flexWrap: 'wrap', marginBottom: '20px' }}>
                    <div className="card" style={{ flex: 1, minWidth: '180px', textAlign: 'center', borderLeft: '3px solid #2ed573' }}>
                      <div style={{ fontSize: '48px', fontWeight: '700', color: '#2ed573' }}>{bestSerija.maxPobjeda}</div>
                      <div style={{ fontSize: '14px', color: '#666688', marginTop: '4px' }}>Niz pobjeda</div>
                    </div>
                    <div className="card" style={{ flex: 1, minWidth: '180px', textAlign: 'center', borderLeft: '3px solid #5378ff' }}>
                      <div style={{ fontSize: '48px', fontWeight: '700', color: '#5378ff' }}>{bestSerija.maxBezPoraza}</div>
                      <div style={{ fontSize: '14px', color: '#666688', marginTop: '4px' }}>Niz bez poraza</div>
                    </div>
                    <div className="card" style={{ flex: 1, minWidth: '180px', textAlign: 'center', borderLeft: '3px solid #e94560' }}>
                      <div style={{ fontSize: '48px', fontWeight: '700', color: '#e94560' }}>{bestSerija.maxBezPobjede}</div>
                      <div style={{ fontSize: '14px', color: '#666688', marginTop: '4px' }}>Niz bez pobjede</div>
                    </div>
                  </div>
                  <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ minWidth: '500px' }}>
                        <thead>
                          <tr>
                            <th style={{ textAlign: 'center' }}>Kolo</th>
                            <th>Datum</th>
                            <th>Protivnik</th>
                            <th style={{ textAlign: 'center' }}>D/G</th>
                            <th style={{ textAlign: 'center' }}>Čunjeva</th>
                            <th style={{ textAlign: 'center' }}>MP</th>
                            <th style={{ textAlign: 'center' }}>Rezultat</th>
                          </tr>
                        </thead>
                        <tbody>
                          {[...utakmiceFilter].sort((a, b) => b.datum?.localeCompare(a.datum)).map(u => (
                            <tr key={u.id} style={{ cursor: 'pointer' }}
                              onClick={() => setAktivna({ strana: 'detaljiUtakmice', utakmicaId: u.id })}>
                              <td style={{ textAlign: 'center', fontWeight: '700', color: '#e94560' }}>{u.kolo}</td>
                              <td>{u.datum}</td>
                              <td>{u.protivnik}</td>
                              <td style={{ textAlign: 'center' }}>
                              <span style={{ fontSize: '11px', fontWeight: '700', padding: '2px 7px', borderRadius: '4px', background: u.domaci ? 'rgba(46,213,115,0.15)' : 'rgba(83,120,255,0.15)', color: u.domaci ? '#2ed573' : '#5378ff' }}>
                                {u.domaci ? '🏠 D' : '✈️ G'}
                              </span>
                            </td>
                              <td style={{ textAlign: 'center', fontWeight: '600' }}>{u.ekipaUkupno}</td>
                              <td style={{ textAlign: 'center' }}>{u.ekipaMP}:{u.protivnikMP ?? (8 - u.ekipaMP)}</td>
                              <td style={{ textAlign: 'center' }}>
                                <span className={'badge ' + (u.ekipaBodovi === 2 ? 'badge-green' : u.ekipaBodovi === 1 ? 'badge-blue' : 'badge-red')}>
                                  {u.ekipaBodovi === 2 ? 'Pobjeda' : u.ekipaBodovi === 1 ? 'Remi' : 'Poraz'} ({u.ekipaBodovi})
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── INDIVIDUALNI ───────────────────────────────────────────────────── */}
      {glavniTab === 'igrac' && (
        <div>
          {/* Odabir igrača */}
          <div className="card" style={{ marginBottom: '20px', padding: '14px 18px' }}>
            <div className="form-group" style={{ margin: 0 }}>
              <label>Odaberi igrača — {filterKlub}</label>
              <select value={odabraniIgrac} onChange={e => setOdabraniIgrac(e.target.value)}
                style={{ minWidth: '240px' }}>
                <option value="">-- odaberi igrača --</option>
                {igraciKluba.sort((a, b) => a.ime.localeCompare(b.ime)).map(ig => (
                  <option key={ig.id} value={ig.id}>{ig.ime}</option>
                ))}
              </select>
            </div>
          </div>

          {!odabraniIgrac && (
            <div className="card empty-state">
              <div className="icon">👤</div>
              <p>Odaberi igrača za prikaz individualnih izvještaja.</p>
            </div>
          )}

          {odabraniIgrac && igracStats && (
            <div>
              {/* Summary statistike */}
              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '20px' }}>
                {[
                  { naziv: 'Utakmica', vrijednost: igracStats.brojUt },
                  { naziv: 'Prosjek', vrijednost: igracStats.prosjekUt, boja: '#e94560' },
                  { naziv: 'Prosjek/set', vrijednost: igracStats.prosjekSet },
                  { naziv: 'Domaći', vrijednost: igracStats.prosjekDomaci || '—', boja: '#2ed573' },
                  { naziv: 'Gost', vrijednost: igracStats.prosjekGost || '—', boja: '#5378ff' },
                  { naziv: 'Max', vrijednost: igracStats.maxUkupno },
                  { naziv: 'Ukupno SP', vrijednost: igracStats.ukupnoSP },
                  { naziv: 'Ukupno MP', vrijednost: igracStats.ukupnoMP },
                  { naziv: 'Ukupno cunjeva', vrijednost: igracStats.ukupnoCunjeva },
                  { naziv: 'Promašaji', vrijednost: igracStats.ukupnoPromasaja },
                ].map(s => (
                  <div key={s.naziv} className="stat-card">
                    <div className="broj" style={s.boja ? { color: s.boja } : {}}>{s.vrijednost}</div>
                    <div className="naziv">{s.naziv}</div>
                  </div>
                ))}
              </div>

              {/* Grafikon igrača */}
              {podaciGrafIgrac.length > 1 && (
                <div className="card" style={{ marginBottom: '20px' }}>
                  <div className="section-title">Kretanje čunjeva — {igraci.find(i => i.id === odabraniIgrac)?.ime}</div>
                  <ResponsiveContainer width="100%" height={260}>
                    <LineChart data={podaciGrafIgrac} margin={{ top: 10, right: 20, left: 0, bottom: 60 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#2a2a4a" />
                      <XAxis dataKey="kolo" stroke="#666688" fontSize={11} angle={-45} textAnchor="end" interval={0} />
                      <YAxis stroke="#666688" fontSize={12} domain={['auto', 'auto']} />
                      <Tooltip content={<CustomTooltip />} />
                      <ReferenceLine y={prosjekIgracLinija} stroke="#5378ff" strokeDasharray="6 3"
                        label={{ value: 'Prosjek: ' + prosjekIgracLinija, fill: '#5378ff', fontSize: 11, position: 'right' }} />
                      <Line type="monotone" dataKey="cunjevi" stroke="#e94560" strokeWidth={2.5} name="Čunjeva"
                        dot={(props) => {
                          const { cx, cy, payload } = props;
                          const boja = payload.bodovi === 2 ? '#2ed573' : payload.bodovi === 1 ? '#5378ff' : '#e94560';
                          return <circle key={'dot-' + payload.kolo} cx={cx} cy={cy} r={5} fill={boja} stroke={boja} />;
                        }}
                        activeDot={{ r: 8 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              )}

              {/* Tabela utakmica po utakmicu */}
              <div className="card" style={{ padding: 0, overflow: 'hidden', marginBottom: '16px' }}>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ minWidth: '720px' }}>
                    <thead>
                      <tr>
                        <th style={{ textAlign: 'center' }}>Kolo</th>
                        <th>Datum</th>
                        <th>Protivnik</th>
                        <th style={{ textAlign: 'center' }}>D/G</th>
                        <th style={{ textAlign: 'center' }}>Takmičenje</th>
                        <th style={{ textAlign: 'center' }}>Pune</th>
                        <th style={{ textAlign: 'center' }}>Čišćenje</th>
                        <th style={{ textAlign: 'center', color: '#e94560' }}>Ukupno</th>
                        <th style={{ textAlign: 'center' }}>Prom.</th>
                        <th style={{ textAlign: 'center' }}>SP</th>
                        <th style={{ textAlign: 'center' }}>MP</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[...utakmiceIgraca].reverse().map(u => {
                        const r = u.rezultati.find(r => r.igracId === odabraniIgrac);
                        return (
                          <tr key={u.id} style={{ cursor: 'pointer' }}
                            onClick={() => setAktivna({ strana: 'detaljiUtakmice', utakmicaId: u.id })}>
                            <td style={{ textAlign: 'center', fontWeight: '700', color: '#e94560' }}>{u.kolo}</td>
                            <td>{u.datum}</td>
                            <td>{u.protivnik}</td>
                            <td style={{ textAlign: 'center' }}>
                              <span style={{ fontSize: '11px', fontWeight: '700', padding: '2px 7px', borderRadius: '4px', background: u.domaci ? 'rgba(46,213,115,0.15)' : 'rgba(83,120,255,0.15)', color: u.domaci ? '#2ed573' : '#5378ff' }}>
                                {u.domaci ? '🏠 D' : '✈️ G'}
                              </span>
                            </td>
                            <td style={{ textAlign: 'center', fontSize: '12px', color: '#888' }}>{u.takmicenje || 'PL'}</td>
                            <td style={{ textAlign: 'center' }}>{r?.ukupnoPune || 0}</td>
                            <td style={{ textAlign: 'center' }}>{r?.ukupnoCis || 0}</td>
                            <td style={{ textAlign: 'center', fontWeight: '700', color: '#e94560', fontSize: '15px' }}>{r?.ukupno || 0}</td>
                            <td style={{ textAlign: 'center', color: '#666688' }}>{r?.promasaji || 0}</td>
                            <td style={{ textAlign: 'center', color: '#2ed573' }}>{r?.sp || 0}</td>
                            <td style={{ textAlign: 'center', color: '#5378ff' }}>{r?.mp || 0}</td>
                          </tr>
                        );
                      })}
                      {utakmiceIgraca.length === 0 && (
                        <tr><td colSpan="11" style={{ textAlign: 'center', padding: '30px', color: '#666688' }}>Nema utakmica</td></tr>
                      )}
                    </tbody>
                    {igracStats && utakmiceIgraca.length > 0 && (
                      <tfoot>
                        <tr style={{ background: '#1a1a2e', borderTop: '2px solid #e94560' }}>
                          <td colSpan="5" style={{ fontWeight: '700', color: '#fff', padding: '10px 14px' }}>UKUPNO / PROSJEK</td>
                          <td style={{ textAlign: 'center', fontWeight: '600' }}>{igracStats.ukupnoPune}</td>
                          <td style={{ textAlign: 'center', fontWeight: '600' }}>{igracStats.ukupnoCis}</td>
                          <td style={{ textAlign: 'center', fontWeight: '700', color: '#e94560', fontSize: '15px' }}>{igracStats.ukupnoCunjeva} ({igracStats.prosjekUt})</td>
                          <td style={{ textAlign: 'center', color: '#666688' }}>{igracStats.ukupnoPromasaja}</td>
                          <td style={{ textAlign: 'center', color: '#2ed573', fontWeight: '600' }}>{igracStats.ukupnoSP}</td>
                          <td style={{ textAlign: 'center', color: '#5378ff', fontWeight: '600' }}>{igracStats.ukupnoMP}</td>
                        </tr>
                      </tfoot>
                    )}
                  </table>
                </div>
              </div>

              <div style={{ display: 'flex', gap: '10px' }}>
                <button className="btn-primary" onClick={exportIgracPDF}>📄 Export PDF — igrač</button>
                <button className="btn-secondary" onClick={() => setAktivna({ strana: 'profil', igracId: odabraniIgrac })}>
                  Profil igrača →
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default Izvjestaji;
